import { WorkflowFileNameSchema } from '@cims-online/shared'
import type { WorkflowFileQueueEntry } from '../types'

let nextEntryId = 0

/**
 * Converts selected files into validated queue entries.
 *
 * Directory selections add one folder entry for each parent directory before
 * its files.
 */
export function enumerateWorkflowFiles(
  files: File[]
): WorkflowFileQueueEntry[] {
  const entries: WorkflowFileQueueEntry[] = []
  const folders = new Set<string>()
  for (const file of files) {
    const path = (file.webkitRelativePath || file.name).split('/')
    for (const segment of path) WorkflowFileNameSchema.parse(segment)
    for (let i = 1; i < path.length; i++) {
      const folderPath = path.slice(0, i)
      const key = folderPath.join('/')
      if (!folders.has(key)) {
        folders.add(key)
        entries.push(queueEntry(folderPath))
      }
    }
    entries.push(queueEntry(path, file))
  }
  return entries
}

/**
 * Creates a queued entry with a unique ID.
 *
 * IDs distinguish separate same-name attempts; they never identify the
 * destination.
 */
function queueEntry(path: string[], file?: File): WorkflowFileQueueEntry {
  return {
    id: String(++nextEntryId),
    path,
    file,
    state: 'queued',
    offset: 0,
    message: null,
  }
}

/**
 * Converts dropped files and directories into validated queue entries,
 * recursing through directories.
 *
 * Browsers return directory entries in batches, so `readEntries` is called
 * until it returns none.
 */
export async function enumerateWorkflowDrop(
  items: DataTransferItemList
): Promise<WorkflowFileQueueEntry[]> {
  const result: WorkflowFileQueueEntry[] = []
  const visit = async (entry: FileSystemEntry, parents: string[]) => {
    WorkflowFileNameSchema.parse(entry.name)
    const path = [...parents, entry.name]
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject)
      )
      result.push(queueEntry(path, file))
    } else if (entry.isDirectory) {
      result.push(queueEntry(path))
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      while (true) {
        const children = await new Promise<FileSystemEntry[]>(
          (resolve, reject) => reader.readEntries(resolve, reject)
        )
        if (!children.length) break
        for (const child of children) await visit(child, path)
      }
    }
  }
  // Drop items become unreadable once the drop event returns, so every root is
  // captured before the first await; the captured entries remain readable.
  const roots = Array.from(
    items,
    (item) => item.webkitGetAsEntry?.() ?? item.getAsFile()
  )
  for (const root of roots) {
    if (root instanceof File) result.push(...enumerateWorkflowFiles([root]))
    else if (root) await visit(root, [])
  }
  return result
}
